import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { errorMessage } from '../api/client';
import { Badge } from '../components/Badge';
import { Field } from '../components/Field';
import { Notice } from '../components/Notice';
import { eventTypeLabel } from '../domain/events';
import { formatDateTime, formatSize } from '../lib/format';
import {
  CONNECTION_LABELS,
  desktop,
  isDesktop,
  type DesktopSettings,
  type OutboxEntry,
  type StorageUsage,
} from './bridge';
import { useSyncStatus } from './useSyncStatus';

/**
 * Écran Hors ligne : ce qui attend d'être remonté, ce qui a échoué, et ce que
 * les rapports emportés occupent sur le disque.
 *
 * Sans cet écran, deux choses restent invisibles jusqu'à ce qu'elles fassent
 * mal : un événement refusé par le serveur, qui disparaîtrait en silence, et
 * un disque qui grossit sans jamais redescendre parce que personne ne retire
 * un suivi qu'il a oublié avoir posé.
 */

function QueueTable({
  entries,
  onRetry,
  onDiscard,
}: {
  entries: OutboxEntry[];
  onRetry?: (id: string) => void;
  onDiscard?: (id: string) => void;
}) {
  if (entries.length === 0) {
    return <div className="tableau__vide">Rien à cet endroit.</div>;
  }

  return (
    <div className="tableau-cadre">
      <table className="tableau">
        <thead>
          <tr>
            <th>Événement</th>
            <th>Survenu le</th>
            <th>Créé ici le</th>
            <th>Motif du refus</th>
            {onRetry !== undefined && <th />}
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td>
                {eventTypeLabel(entry.type)}
                <div className="faible">
                  <Link to={`/postes/${entry.workstationId}`}>Voir le poste</Link>
                  {entry.reason !== '' && ` · ${entry.reason}`}
                </div>
              </td>
              <td>{formatDateTime(entry.occurredAt)}</td>
              <td>{formatDateTime(entry.createdAt)}</td>
              <td>
                {entry.failureReason === null ? (
                  <span className="faible">
                    {entry.attempts === 0
                      ? 'Pas encore tenté'
                      : `${entry.attempts} tentative(s)`}
                  </span>
                ) : (
                  <>
                    <Badge tone="danger">{entry.failureStatus ?? '—'}</Badge>{' '}
                    {entry.failureReason}
                  </>
                )}
              </td>
              {onRetry !== undefined && (
                <td className="ligne">
                  <button
                    type="button"
                    className="bouton bouton--petit"
                    onClick={() => onRetry(entry.id)}
                  >
                    Remettre en attente
                  </button>
                  {onDiscard !== undefined && (
                    <button
                      type="button"
                      className="bouton bouton--fantome bouton--petit"
                      onClick={() => onDiscard(entry.id)}
                    >
                      Abandonner
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function OfflinePage() {
  const status = useSyncStatus();
  const [queue, setQueue] = useState<{ pending: OutboxEntry[]; failed: OutboxEntry[] }>({
    pending: [],
    failed: [],
  });
  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const [settings, setSettings] = useState<DesktopSettings | null>(null);
  const [serverUrl, setServerUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback((): void => {
    if (!isDesktop()) return;
    const bridge = desktop();
    Promise.all([bridge.outbox.list(), bridge.reports.usage(), bridge.settings.read()])
      .then(([list, storage, current]) => {
        setQueue(list);
        setUsage(storage);
        setSettings(current);
        setServerUrl(current.serverUrl);
      })
      .catch((cause: unknown) => setError(errorMessage(cause)));
  }, []);

  useEffect(reload, [reload]);
  // la file bouge quand la synchronisation avance : on la relit à chaque état
  useEffect(reload, [reload, status?.pending, status?.failed, status?.state]);

  if (!isDesktop()) {
    return (
      <Notice tone="info" title="Écran propre à l'application de bureau">
        Le mode hors ligne n'existe que dans l'application installée sur le poste.
        Dans le navigateur, l'interface parle directement au serveur.
      </Notice>
    );
  }

  const act = (run: Promise<unknown>, message: string): void => {
    setError(null);
    run
      .then(() => {
        setNotice(message);
        reload();
      })
      .catch((cause: unknown) => setError(errorMessage(cause)));
  };

  const onUnfollow = (workstationId: string, name: string): void => {
    const horsLigne = status?.state === 'horsLigne';
    // Hors ligne, la suppression est sans retour possible avant que le réseau
    // revienne : les fichiers ne peuvent pas être retéléchargés.
    if (horsLigne) {
      const confirme = window.confirm(
        `Ce poste est hors ligne. Retirer le suivi de « ${name} » supprime ses ` +
          'rapports du disque, et ils ne pourront pas être récupérés avant le ' +
          'retour du réseau.\n\nRetirer quand même ?',
      );
      if (!confirme) return;
    }
    act(
      desktop().reports.unfollow(workstationId),
      `Suivi retiré : les rapports de « ${name} » ont été supprimés du disque.`,
    );
  };

  const onFreeAll = (): void => {
    const confirme = window.confirm(
      'Supprimer tous les rapports emportés et retirer tous les suivis ?\n\n' +
        "Les fichiers seront redescendus au prochain suivi, s'il y a du réseau.",
    );
    if (!confirme) return;
    act(desktop().reports.freeAll(), 'Tous les rapports emportés ont été supprimés.');
  };

  return (
    <div className="pile">
      <div className="entete-page">
        <div>
          <h1>Hors ligne</h1>
          <p className="entete-page__sous-titre">
            Ce qui attend d'être remonté, et ce que ce poste garde sur son disque.
          </p>
        </div>
        <div className="ligne">
          {status !== null && (
            <Badge tone={status.state === 'horsLigne' ? 'danger' : 'ok'}>
              {CONNECTION_LABELS[status.state]}
            </Badge>
          )}
          <button
            type="button"
            className="bouton bouton--primaire"
            onClick={() => act(desktop().sync.now(), 'Synchronisation lancée.')}
          >
            Synchroniser maintenant
          </button>
        </div>
      </div>

      {error !== null && <Notice tone="erreur">{error}</Notice>}
      {notice !== null && <Notice tone="info">{notice}</Notice>}

      {status !== null && !status.authenticated && (
        <Notice tone="alerte" title="Session refusée par le serveur">
          Le serveur répond, mais n'accepte plus la session de ce poste. Rien ne
          remontera tant que vous ne vous serez pas reconnecté. Ce n'est pas une
          absence de réseau : attendre n'y changera rien.
        </Notice>
      )}

      {status?.lastError !== null && status?.lastError !== undefined && (
        <Notice tone="alerte" title="Dernier échange">
          {status.lastError}
        </Notice>
      )}

      <section className="panneau">
        <div className="panneau__entete">
          <span className="panneau__titre">En attente de remontée</span>
          <span className="faible pousse">
            {queue.pending.length} événement(s), dans leur ordre de création
          </span>
        </div>
        <div className="panneau__corps panneau__corps--nu">
          <QueueTable entries={queue.pending} />
        </div>
      </section>

      <section className="panneau">
        <div className="panneau__entete">
          <span className="panneau__titre">Refusés par le serveur</span>
          <span className="faible pousse">
            Ils ne seront pas renvoyés d'eux-mêmes et ne bloquent pas la file
          </span>
        </div>
        <div className="panneau__corps panneau__corps--nu">
          {queue.failed.length > 0 && (
            <div style={{ padding: 'var(--espace-5)' }}>
              <Notice tone="alerte" title="Ces événements n'ont pas été enregistrés">
                Une règle de cohérence du serveur les a refusés. Leur effet a été
                retiré de l'affichage de ce poste : ce que vous voyez à l'écran est
                bien ce que le serveur connaît.
              </Notice>
            </div>
          )}
          <QueueTable
            entries={queue.failed}
            onRetry={(id) =>
              act(desktop().outbox.retry(id), 'Événement remis en attente.')
            }
            onDiscard={(id) => {
              if (!window.confirm('Abandonner définitivement cet événement ?')) return;
              act(desktop().outbox.discard(id), 'Événement abandonné.');
            }}
          />
        </div>
      </section>

      <section className="panneau">
        <div className="panneau__entete">
          <span className="panneau__titre">Rapports emportés</span>
          {usage !== null && (
            <span className="faible pousse">
              {usage.totalFiles} fichier(s) · {formatSize(usage.totalSizeBytes)}
            </span>
          )}
          <button
            type="button"
            className="bouton bouton--petit"
            onClick={onFreeAll}
            disabled={usage === null || usage.totalFiles === 0}
          >
            Tout libérer
          </button>
        </div>
        <div className="panneau__corps panneau__corps--nu">
          {usage !== null && usage.workstations.length === 0 && (
            <div className="tableau__vide">
              Aucun poste suivi. Le bouton « Emporter les rapports » de la fiche d'un
              poste télécharge ses rapports et garde les suivants.
            </div>
          )}
          {usage !== null && usage.workstations.length > 0 && (
            <div className="tableau-cadre">
              <table className="tableau">
                <thead>
                  <tr>
                    <th>Poste</th>
                    <th>Suivi depuis</th>
                    <th className="tableau__nombre">Fichiers</th>
                    <th className="tableau__nombre">À descendre</th>
                    <th className="tableau__nombre">Espace</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {usage.workstations.map((entry) => (
                    <tr key={entry.workstationId}>
                      <td>
                        <Link to={`/postes/${entry.workstationId}`}>{entry.name}</Link>
                      </td>
                      <td>{formatDateTime(entry.followedAt)}</td>
                      <td className="tableau__nombre">{entry.fileCount}</td>
                      <td className="tableau__nombre">
                        {entry.missingCount === 0 ? (
                          <span className="faible">—</span>
                        ) : (
                          entry.missingCount
                        )}
                      </td>
                      <td className="tableau__nombre">{formatSize(entry.sizeBytes)}</td>
                      <td>
                        <button
                          type="button"
                          className="bouton bouton--petit"
                          onClick={() => onUnfollow(entry.workstationId, entry.name)}
                        >
                          Retirer le suivi
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {usage !== null && usage.orphanCount > 0 && (
            <div style={{ padding: 'var(--espace-5)' }}>
              <Notice tone="info">
                {usage.orphanCount} fichier(s) sans poste suivi occupent{' '}
                {formatSize(usage.orphanSizeBytes)}. « Tout libérer » les supprime.
              </Notice>
            </div>
          )}
        </div>
      </section>

      <section className="panneau">
        <div className="panneau__entete">
          <span className="panneau__titre">Serveur</span>
        </div>
        <div className="panneau__corps pile pile--serree">
          <Field
            label="Adresse du serveur"
            hint={`Fuseau de référence des filtres par plage horaire : ${
              settings?.timeZone ?? '—'
            }. Il doit valoir celui du serveur.`}
          >
            <input
              type="url"
              value={serverUrl}
              placeholder="https://parc.example.com"
              onChange={(event) => setServerUrl(event.target.value)}
            />
          </Field>
          <div className="ligne">
            <button
              type="button"
              className="bouton"
              disabled={settings !== null && serverUrl === settings.serverUrl}
              onClick={() =>
                act(desktop().settings.write({ serverUrl }), 'Adresse enregistrée.')
              }
            >
              Enregistrer
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
